import { Button } from "@/components/ui/button";
import {
  useDeleteInsight,
  useDeleteUnassignedInsights,
  useDuplicateInsight,
  useInsights,
  useUpdateInsight,
} from "@/hooks/agent/useInsights";
import { Insight } from "@/services/api/InsightService";
import { LeadInfoField } from "@/types/leads/lead-info";
import { InsightList } from "./InsightList";

interface InsightManagerProps {
  onInsightSelect?: (insight: Insight) => void;
  limit?: number;
  agentName?: string;
}

export function InsightManager({ onInsightSelect, limit = 30, agentName }: InsightManagerProps) {
  const { data: insights = [], isLoading, hasNextPage, fetchNextPage, totalCount } = useInsights(limit);

  const updateInsight = useUpdateInsight();
  const deleteInsight = useDeleteInsight();
  const deleteUnassigned = useDeleteUnassignedInsights();
  const duplicateInsight = useDuplicateInsight();

  const handleUpdateInsight = async (
    id: number,
    updates: { name?: string; description?: string; fields?: LeadInfoField[]; is_default?: boolean },
  ) => {
    await updateInsight.mutateAsync({ id, updates });
  };

  const handleDeleteInsight = async (id: number) => {
    await deleteInsight.mutateAsync(id);
  };

  const handleDuplicateInsight = async (insight: Insight) => {
    await duplicateInsight.mutateAsync(insight);
  };

  if (isLoading) {
    return <div className="p-4 text-center text-gray-500">Loading insights...</div>;
  }

  const unassignedInsightIds = insights
    .filter((insight) => !insight.is_default && (!insight.dependent_agents || insight.dependent_agents.length === 0))
    .map((insight) => insight.id);
  const unassignedCount = unassignedInsightIds.length;

  return (
    <div className="space-y-4 -mt-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-full">
          <span className="font-semibold text-gray-900">{totalCount}</span> total
        </span>
        <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-full">
          <span className="font-semibold text-gray-900">{unassignedCount}</span> unassigned
        </span>
        {agentName && (
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            Current genie:
            <span className="bg-primary/10 text-primary px-2.5 py-1 rounded-full font-medium">{agentName}</span>
          </span>
        )}
        {unassignedCount > 0 && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={deleteUnassigned.isPending}
            onClick={() => deleteUnassigned.mutate(unassignedInsightIds)}
            className="ml-auto text-red-600 border-red-200 hover:bg-red-50 text-xs h-7"
          >
            {deleteUnassigned.isPending ? "Deleting..." : "Delete all unassigned"}
          </Button>
        )}
      </div>

      <InsightList
        insights={insights}
        onSelect={onInsightSelect}
        onUpdate={handleUpdateInsight}
        onDelete={handleDeleteInsight}
        onDuplicate={handleDuplicateInsight}
      />

      {hasNextPage && (
        <Button type="button" variant="outline" onClick={() => fetchNextPage()} className="w-full">
          Load More Insights
        </Button>
      )}
    </div>
  );
}
